import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { ProductCard } from '../components/common/ProductCard';
import { LeadFormDialog } from '../components/common/LeadFormDialog';
import { getCategories, getProducts, getBrands } from '../lib/api';
import { Search, Filter, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export default function ProductsPage() {
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchQuery(q);
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          getCategories(),
          getBrands(),
        ]);
        setCategories(catRes.data);
        setBrands(brandRes.data);

        const pageSize = 5000;
        let skip = 0;
        const merged = [];
        for (;;) {
          const prodRes = await getProducts({ limit: pageSize, skip });
          const batch = prodRes.data || [];
          merged.push(...batch);
          if (batch.length < pageSize) break;
          skip += pageSize;
        }
        setProducts(merged);

        const brandParam = searchParams.get('brand');
        if (brandParam) {
          const match = brandRes.data.find(
            (b) => b.slug === brandParam || b.id === brandParam
          );
          if (match) setSelectedBrand(match.id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setSelectedBrand('all');
  };

  const brandsInCategory = useMemo(() => {
    if (selectedCategory === 'all') return brands;
    const brandIds = new Set(
      products
        .filter((p) => p.category_id === selectedCategory)
        .map((p) => p.brand_id)
    );
    return brands.filter((b) => brandIds.has(b.id));
  }, [brands, products, selectedCategory]);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
    const matchesBrand = selectedBrand === 'all' || product.brand_id === selectedBrand;
    return matchesSearch && matchesCategory && matchesBrand;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedBrand('all');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedBrand !== 'all';

  return (
    <>
      <SEOHead
        title="Products - Premium Fragrances & Cosmetics Catalog"
        description="Browse our complete catalog of luxury perfumes, deodorants, body mists, gift sets, attars, and cosmetics. Wholesale pricing available."
        canonical="https://silverperfumes.thevinculum.org.in/products"
      />

      <section className="pt-32 pb-12 bg-[#0A0A0A]" data-testid="products-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'Products' }]} />
          <div className="mt-4">
            <h1 className="heading-xl text-white mb-4">Our Products</h1>
            <p className="text-gray-300 max-w-xl">
              Explore our comprehensive catalog of premium fragrances and beauty products 
              from the world's most prestigious brands.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F4F4F5]" data-testid="products-catalog">
        <div className="container-custom">
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 input-luxury bg-white"
                  data-testid="product-search"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 btn-secondary"
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>

              <div className="hidden md:flex gap-4">
                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="w-48 bg-white" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  key={selectedCategory}
                  value={selectedBrand}
                  onValueChange={setSelectedBrand}
                >
                  <SelectTrigger className="w-48 bg-white" data-testid="brand-filter">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    {brandsInCategory.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#C0C4CC]"
                  >
                    <X className="h-4 w-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {showFilters && (
              <div className="md:hidden flex flex-col gap-4 mt-4 p-4 bg-white">
                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  key={selectedCategory}
                  value={selectedBrand}
                  onValueChange={setSelectedBrand}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All brands</SelectItem>
                    {brandsInCategory.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <button onClick={clearFilters} className="btn-secondary">
                    Clear Filters
                  </button>
                )}
              </div>
            )}
          </div>

          <p className="text-gray-500 mb-6">
            {hasActiveFilters
              ? `Showing ${filteredProducts.length} of ${products.length} products`
              : `Showing ${products.length} products`}
          </p>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="skeleton h-72" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {filteredProducts.map((product) => {
                const category = categories.find(c => c.id === product.category_id);
                const brand = brands.find(b => b.id === product.brand_id);
                return (
                  <ProductCard
                    key={product.id}
                    product={product}
                    category={category?.name}
                    categorySlug={category?.slug}
                    brand={brand?.name}
                    brandSlug={brand?.slug}
                  />
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No products found matching your criteria.</p>
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          )}

          <div className="mt-16 text-center p-12 bg-white">
            <h3 className="heading-md mb-4">Can't Find What You're Looking For?</h3>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              We have access to thousands more products. Request a custom quote and our team 
              will help you find exactly what you need.
            </p>
            <button 
              onClick={() => setShowQuoteForm(true)}
              className="btn-primary"
              data-testid="products-quote-btn"
            >
              Request Custom Quote
            </button>
          </div>
        </div>
      </section>

      <LeadFormDialog
        isOpen={showQuoteForm}
        onClose={() => setShowQuoteForm(false)}
        sourcePageUrl="/products"
        showBuyerType={true}
      />
    </>
  );
}
